import React, { useContext, useReducer, useRef } from 'react'
import Slider from 'react-slick'
import scrollIntoView from 'scroll-into-view'

import { userContext } from 'global/context/sub-contexts/userContext'
import { legacyImageURL } from 'components/LegacyImage'

import StartSlide from 'steps/StartSlide'
import SkipButton from 'steps/SkipButton'
import ArrowButton from 'steps/ArrowButton3'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import IntersectionVisible from 'components/IntersectionVisible'

import ErrorScreen from './ErrorScreen'
import ProductSelectorScreen from './ProductSelectorScreen'
import SubmitProductReviewScreen from './SubmitProductReviewScreen'
import SubmitProductReviewThankYouScreen from './SubmitProductReviewThankYouScreen'
import ReminderScreen from './ReminderScreen'
import ReminderThankYouScreen from './ReminderThankYouScreen'

import {
  start as serviceStart,
  complete as serviceComplete,
  reminder as submitReminder,
  submit as submitReview,
} from 'services/review'
import analytics from 'global/AnalyticsData'

/*

Areas of confusion
products have 2 ids:
    `id` - an internal beaker id that we NEVER use
    `productId` - an id that Bazaarvoice uses that we ALWAYS use

*/

function reducer(state, action) {
  // console.log("action type: " + action.type)
  switch (action.type) {
    case 'step-start':
      return {
        nonce: action.nonce,
        screen: 'product-selector',
        arrowVisible: false,
        skipVisible: true,
      }

    case 'product-selected':
      return {
        nonce: state.nonce,
        screen: 'product-review',
        productId: action.productId,
        arrowVisible: false,
        skipVisible: true,
      }

    case 'product-skipped':
      return {
        nonce: state.nonce,
        screen: 'reminder',
        arrowVisible: false,
        skipVisible: true,
        email: action.email,
      }

    case 'product-review-submitted':
      return {
        nonce: state.nonce,
        screen: 'product-review-thankyou',
        productId: action.productId,
        arrowVisible: true,
        skipVisible: false,
      }

    case 'reminder-submit':
      return {
        nonce: state.nonce,
        screen: 'reminder-thankyou',
        productId: action.productId,
        productTitle: action.productTitle,
        productImageUrl: action.productImageUrl,
        email: action.email,
        arrowVisible: true,
        skipVisible: false,
      }

    case 'error':
      return {
        nonce: state.nonce,
        screen: 'error',
        error: action.error,
        arrowVisible: true,
        skipVisible: false,
      }

    default:
      console.warn(`Unsupported action.type: ${action.type}`)
  }
}

const COMPLETE_BY_SKIP = true
const COMPLETE_NO_SKIP = false

const ReviewStep = ({
  breakId,
  step,
  onGoToNext,
  onStepStart,
  onStepComplete,
  onStepSkip,
  scrollComplete,
  isComplete,
  HtmlFooter,
}) => {
  const { getProfile } = useContext(userContext)

  let initialState = {
    screen: 'product-selector',
    arrowVisible: false,
    skipVisible: false,
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const selfRef = useRef(null)
  const sliderRef = useRef(null)
  const sentStartRef = useRef(false)

  let nonce = undefined

  const sliderSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
  }

  const setIsWaiting = isWaiting => {
    if (isWaiting) {
      selfRef.current.classList.remove('waiting-false')
      selfRef.current.classList.add('waiting-true')
    } else {
      selfRef.current.classList.remove('waiting-true')
      selfRef.current.classList.add('waiting-false')
    }
  }

  const scrollToTopOfStepUI = () => {
    // selfRef.current && selfRef.current.scrollIntoView({ behavior: "smooth", block: "start" })

    // if (selfRef.current) {
    //     let headerOffset = 97;  // don't love hard-coding this...
    //     var elementPosition = selfRef.current.getBoundingClientRect().top;
    //     var offsetPosition = elementPosition - headerOffset ; // + 20;

    //     window.scrollTo({
    //         top: offsetPosition,
    //         behavior: "smooth"
    //     });

    // }

    return new Promise(function (resolve) {
      scrollIntoView(selfRef.current, {}, () => {
        resolve()
      })
    })
  }

  // this will get called by the start screen button AND by scrolling into view  with no start screen
  const handleStartClick = () => {
    if (step.hasStartScreen) {
      sliderRef.current.slickNext()
    }

    serviceStart(breakId, step.id)
      .then(async result => {
        if (result && result.detail) {
          nonce = result.detail.nonce

          dispatch({
            type: 'step-start',
            attempt: result.detail.attempt,
            nonce: result.detail.nonce,
          })
        }

        if (onStepStart) {
          const { attempt, firstBreakEngagement } = result
          onStepStart(step, attempt, firstBreakEngagement)
        }
      })
      .catch(e => {
        console.error(e)
        // TODO: how do we handle a failure at the start?
        // dispatch({
        //     type: 'error',
        //     error: e.detail
        // })
        // for now let them continue so we can present a UI and capture info
        dispatch({
          type: 'step-start',
          attempt: undefined,
          nonce: undefined,
        })
      })
  }

  const handleProductSelect = async (id, label) => {
    await scrollToTopOfStepUI()

    dispatch({
      type: 'product-selected',
      productId: id,
    })
  }

  // user did not select a product to review
  const handleProductSkip = async () => {
    await scrollToTopOfStepUI()

    // want want to pre-fill the email form field
    // so we look it up here and pass it in
    let profile = getProfile()
    dispatch({
      type: 'product-skipped',
      email: profile.guest ? '' : profile.email,
    })
  }

  // user submitted a review
  const handleProductReviewSubmit = async (
    productId,
    rating,
    headline,
    comments,
    nickname,
    email,
    ioBlackBox,
    reviewSessionId,
  ) => {
    const product = step.review.products.find(product => {
      return product.productId === productId
    })

    // try {
    setIsWaiting(true)
    let response = await submitReview(
      breakId,
      step.review.id,
      productId,
      rating,
      headline,
      comments,
      nickname,
      email,
      ioBlackBox,
      reviewSessionId,
    )
    analytics.stepEvent('step_interaction_review_submit', step, {
      result_id: productId,
      result: product.productSelectorTitle,
    })

    setIsWaiting(false)

    if (response.status === 200) {
      scrollToTopOfStepUI()
      dispatch({
        type: 'product-review-submitted',
        productId,
        rating,
        headline,
        comments,
        nickname,
        email,
      })
    } else if (response.status === 400) {
      // console.log(response.status);
      // let json = JSON.parse(body)
      let json = undefined
      let responseText = await response.text()
      try {
        json = JSON.parse(responseText)
      } catch (e) {
        // we got an error, but not well-formed json. show it as a general form error.
        // hack: formik has no concept of generic form error messages
        // so attach this error to the bottom-most field, shaped like we expect.
        json = {
          usernickname: {
            Message: responseText,
          },
        }
      }
      return json
    } else {
      scrollToTopOfStepUI()
      dispatch({
        type: 'error',
        error: response,
      })
    }

    // } catch (e) {
    //     // TODO: error handling
    //     console.log(e)
    //     scrollToTopOfStepUI()
    //     dispatch({
    //         type: 'error',
    //         error: e.detail             // we assume the server sent a 4xx with detail
    //     })
    // }
  }

  // visitor opted in to receiving a reminder
  const handleReminderSubmit = async (email, productId) => {
    console.log('handleReminderSubmit ' + productId + ' ' + email)

    const product = step.review.products.find(product => {
      return product.productId === productId
    })

    try {
      setIsWaiting(true)
      await submitReminder(breakId, step.review.id, productId, email)
      analytics.stepEvent('step_interaction_reminder_submit', step, {
        result_id: productId,
        result: product.productSelectorTitle,
      })

      setIsWaiting(false)

      scrollToTopOfStepUI()
      dispatch({
        type: 'reminder-submit',
        productId: product.productId,
        productTitle: product.title,
        productImageUrl: legacyImageURL(product.image),
        email: email,
      })
    } catch (e) {
      setIsWaiting(false)
      scrollToTopOfStepUI()
      dispatch({
        type: 'error',
        error: e.detail, // we assume the server sent a 4xx with detail
      })
    }
  }

  const handleArrowClick = () => {
    serviceComplete(breakId, step.id, nonce, COMPLETE_NO_SKIP).catch(e => {
      // this prevents an uncaught exception from bombing the page. more diagnostics would be nice.
      console.error('/service/complete', e)
    })

    onStepComplete && onStepComplete(step.id)
    onGoToNext && onGoToNext(step.id)
  }

  const handleSkipClick = () => {
    serviceComplete(breakId, step.id, nonce, COMPLETE_BY_SKIP).catch(e => {
      // this prevents an uncaught exception from bombing the page. more diagnostics would be nice.
      console.error('/service/complete', e)
    })

    onStepSkip && onStepSkip(step.id)
  }

  // our intersection observer triggered on/off screen
  const handleIsOnScreen = isIntersecting => {
    if (
      !step.hasStartScreen &&
      isIntersecting &&
      state.screen === 'product-selector'
    ) {
      handleStartClick()
      sentStartRef.current = true
    }
  }

  //listen for scrollComplete and fire off handleCompleteClick when toggled
  React.useEffect(() => {
    const cleanup = async () => {
      const response = await serviceComplete(
        breakId,
        step.id,
        nonce,
        COMPLETE_BY_SKIP,
      )
    }

    if (scrollComplete && !isComplete) {
      cleanup()
    }
  }, [scrollComplete, isComplete])

  let style = {}
  const bgImg =
    step && step.backgroundImage
      ? step.backgroundImage.id
      : step.backgroundImageId

  if (step && step.backgroundColor) {
    style.backgroundColor = step.backgroundColor
  }

  if (bgImg) {
    style.backgroundImage = `url(${process.env.SITE_ASSET_URL}/${bgImg})`
  }

  return (
    <div className="sponsored-flow-panel " style={style} ref={selfRef}>
      <section className={`continue-ready`}>
        <>
          <Slider
            ref={slider => (sliderRef.current = slider)}
            {...sliderSettings}
          >
            {step.hasStartScreen && (
              <StartSlide
                headline={step.startHeader}
                para={step.startBody}
                footer={step.startFooter}
                backgroundImageId={step.startBackgroundImageId}
                backgroundColor={step.startBackgroundColor}
                onStartClicked={handleStartClick}
                stepType={step.type}
                // bgImageExists={style.backgroundImage && true}
              />
            )}
            <div>
              {/* for slick */}
              <IntersectionVisible
                active={true}
                options={{
                  threshold: 0.4,
                }}
                onIntersect={handleIsOnScreen}
              >
                <div className={`review-step state--${state.screen}`}>
                  {state.screen === 'product-selector' && (
                    <ProductSelectorScreen
                      review={step.review}
                      onSelect={handleProductSelect}
                      onSkip={handleProductSkip}
                    />
                  )}
                  {state.screen === 'reminder' && (
                    <ReminderScreen
                      review={step.review}
                      defaultProductId={state.productId}
                      defaultEmail={state.email}
                      onSubmit={handleReminderSubmit}
                    />
                  )}
                  {state.screen === 'reminder-thankyou' && (
                    <ReminderThankYouScreen
                      review={step.review}
                      email={state.email}
                      productTitle={state.productTitle}
                      productImageUrl={state.productImageUrl}
                      reminderDate={state.reminderDate}
                    />
                  )}
                  {state.screen === 'product-review' && (
                    <SubmitProductReviewScreen
                      review={step.review}
                      defaultProductId={state.productId}
                      onFormSubmit={handleProductReviewSubmit}
                    />
                  )}
                  {state.screen === 'product-review-thankyou' && (
                    <SubmitProductReviewThankYouScreen
                      review={step.review}
                      productId={state.productId}
                    />
                  )}

                  {state.screen === 'error' && (
                    <ErrorScreen review={step.review} error={state.error} />
                  )}

                  <div className="review-step--footer"></div>
                  {state.skipVisible && (
                    <span>
                      <SkipButton onClick={handleSkipClick} step_id={step.id} />
                    </span>
                  )}
                  <ArrowButton
                    visible={state.arrowVisible}
                    clickable={true}
                    onClick={handleArrowClick}
                  />
                </div>
              </IntersectionVisible>
            </div>
          </Slider>
          <div className="sponsored-flow-panel--footer"></div>
          {HtmlFooter && <HtmlFooter />}
        </>
      </section>
    </div>
  )
}

export default ReviewStep
